import { PatientStatus, RoleWithOrg } from "@sequel-care/types"
import { getAllPatients } from "api"
import { getPatientForEdit } from "api/Patient"
import { Button, Tooltip } from "components/common"
import EditIcon from "components/icons/Edit"
import { AvatarField } from "components/Settings/ItemList"
import { ListField } from "components/Settings/types"
import { ListPatient } from "pages/patients"
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { TbUserOff, TbUserPlus, TbTrash } from "react-icons/tb"
import { useTranslation } from "react-i18next"
import { useDispatch } from "store"
import { setTreatmentEnd } from "store/global/actions"
import { openPatientModal, openTreatmentEndModal } from "store/modal/actions"
import { SkeletonPlaceholder } from "types/Misc"
import { classNames } from "utils"
import { StatusField, TagsField } from "./RowComponents"
import DeletePatientModal from "modals/DeletePatientModal/DeletePatientModal"
import { useCurrentUser } from "store/hooks"

export const patientListFields: ListField<ListPatient>[] = [
    {
        key: "user",
        RenderField: AvatarField
    },
    {
        key: "status",
        RenderField: StatusField
    },
    {
        key: "tags",
        RenderField: TagsField
    }
]

export type PatientFilters = {
    therapist_ids: number[]
    status?: PatientStatus
    tags: number[]
    query: string
}

export const EditPatientAction = (
    role: RoleWithOrg,
    setPatients?: Dispatch<SetStateAction<ListPatient[]>>,
    onUpdated?: () => void
) => {
    const dispatch = useDispatch()
    const { t } = useTranslation("patient")
    let latestRequest = 0

    return ({ item }: { item: ListPatient | SkeletonPlaceholder }) => {
        const [loading, setLoading] = useState(false)
        const mounted = useRef(false)
        useEffect(() => {
            mounted.current = true
            return () => {
                mounted.current = false
            }
        }, [])
        const isSkeleton = "skeleton" in item
        const isUser = "user" in item
        if (
            !isSkeleton &&
            role.role !== "admin" &&
            !item.collaborators.some(
                ({ user, permissions }) =>
                    user.id === role.user_id && permissions.some(({ permission }) => permission === "case_manager")
            )
        )
            return null

        const onClick = async () => {
            if (isSkeleton || loading) return

            const request = ++latestRequest
            setLoading(true)
            try {
                const patientFile = await getPatientForEdit(item.id)
                if (!patientFile || !mounted.current || request !== latestRequest) return

                dispatch(
                    openPatientModal({
                        patientFile,
                        onUpdate:
                            onUpdated ??
                            (setPatients ? () => getAllPatients().then((res) => setPatients(res)) : undefined)
                    })
                )
            } catch {
                // ApiGet already displays the request error.
            } finally {
                if (mounted.current) setLoading(false)
            }
        }

        return (
            <Tooltip content={t("patientActions.editTooltip")} placement="bottom">
                <EditIcon
                    data-testid={isUser ? `edit-patient-btn-${item.user.id}` : "edit-patient-btn"}
                    className={classNames("w-5 h-5", !isSkeleton && "cursor-pointer", loading && "opacity-50")}
                    onClick={onClick}
                />
            </Tooltip>
        )
    }
}

export const DeletePatientAction =
    (onDeleted?: () => void) =>
    ({ item }: { item: ListPatient }) => {
        const [isOpen, setIsOpen] = useState(false)
        const currentUser = useCurrentUser()
        const { t } = useTranslation("patient")
        const isSkeleton = "skeleton" in item
        if (currentUser.id !== item.user_id) {
            return (
                <>
                    <Tooltip content={t("patientActions.deleteTooltip")} placement="bottom">
                        <TbTrash
                            onClick={() => setIsOpen(true)}
                            className={classNames("w-5 h-5", !isSkeleton && "cursor-pointer")}
                            data-testid={
                                isSkeleton ? "toggle-treatment-btn-skeleton" : `toggle-treatment-btn-${item.id}`
                            }
                        />
                    </Tooltip>
                    <DeletePatientModal
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                        onDeleted={onDeleted}
                        params={{ user_id: item?.user_id, email: item?.user?.email }}
                    />
                </>
            )
        } else return null
    }

export const ToggleTreatmentStatusAction = (
    role: RoleWithOrg,
    setPatients?: Dispatch<SetStateAction<ListPatient[]>>,
    onUpdated?: () => void
) => {
    const dispatch = useDispatch()
    const { t } = useTranslation("patient")
    const [loadingPatients, setLoadingPatients] = useState<Set<number>>(new Set())

    return ({ item }: { item: ListPatient | SkeletonPlaceholder }) => {
        const isSkeleton = "skeleton" in item
        const isLoading = !isSkeleton && loadingPatients.has(item.id)

        const openModal = () => {
            if (isSkeleton) return

            dispatch(
                openTreatmentEndModal({
                    patient_id: item.id,
                    fromPatientScope: false,
                    setPatients,
                    onUpdated,
                    initialState: item.treatment_end
                        ? { reason: item.treatment_end_reason, date: new Date(item.treatment_end) }
                        : undefined
                })
            )
        }

        const activatePatient = async (item: ListPatient) => {
            setLoadingPatients((prev) => new Set(prev).add(item.id))
            const updated = await dispatch(
                setTreatmentEnd(item.id, { date: null, reason: undefined }, false, setPatients)
            )
            if (updated) onUpdated?.()
            setLoadingPatients((prev) => {
                const newSet = new Set(prev)
                newSet.delete(item.id)
                return newSet
            })
        }

        if (
            role.role === "admin" ||
            (item as ListPatient).collaborators.some(({ permissions }) => {
                return permissions?.some(
                    ({ user_id, permission }) => user_id == role.user_id && permission === "case_manager"
                )
            })
        ) {
            if (!isSkeleton && item.treatment_end)
                return (
                    <Tooltip content={t("patientActions.activatePatientTooltip")} placement="bottom">
                        <Button loading={isLoading} onClick={() => activatePatient(item)}>
                            <TbUserPlus className="w-5 h-5" />
                        </Button>
                    </Tooltip>
                )

            return (
                <Tooltip content={t("patientActions.deactivatePatientTooltip")} placement="bottom">
                    <TbUserOff
                        onClick={openModal}
                        className={classNames("w-5 h-5", !isSkeleton && "cursor-pointer")}
                        data-testid={isSkeleton ? "toggle-treatment-btn" : `toggle-treatment-btn-${item.id}`}
                    />
                </Tooltip>
            )
        } else return null
    }
}
